import type { LoadFunction } from 'ol/Image';
import { memoryDiagnostics } from '@ais-ui/utils';

interface CanvasReference {
  current: HTMLCanvasElement | null;
}

interface PendingImage {
  image: HTMLImageElement;
  onLoad: () => void;
  onError: () => void;
  completeDecode: (() => void) | undefined;
}

// OpenLayers always calls `decode(image)` on whatever image this loader is
// given, using only the `src` this loader assigned. An image left without a
// `src` never fires load/error in a real browser, so its ImageWrapper (and
// every closure OL attached while waiting for it) is retained forever. Every
// call must therefore assign a `src`, even when we intentionally don't draw.
const TRANSPARENT_PIXEL_SRC =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

// Redundant/mismatched-geometry requests must resolve without ever reaching
// ImageState.LOADED: OpenLayers' canvas renderer swaps its rendered image to
// any wrapper that loads successfully, which would remap the *previous*
// overlay's pixels onto the *new* (wrong) extent. Deliberately failing the
// load instead settles OL's decode() promise (no hang/retention) while
// ImageState.ERROR leaves the renderer showing the last correctly
// geo-referenced frame, exactly like a real backend failure would.
const UNRESOLVABLE_IMAGE_SRC = 'data:,';

function geometryEquals(a: number[], b: number[]) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function createDiagnosticOwner() {
  return memoryDiagnostics.isRunning
    ? memoryDiagnostics.createOverlayOwner()
    : undefined;
}

/** Owns one hit-test canvas and at most one pending source image. */
export function createOverlayImageLoader(
  canvasRef: CanvasReference,
  refreshSelectedTargets: () => void,
) {
  let canvas: HTMLCanvasElement | null = null;
  let overlay: string | null = null;
  let pending: PendingImage | null = null;
  let consumed = false;
  let requestGeometry: number[] | null = null;
  let generation = 0;
  let disposed = false;
  let diagnosticOwner = createDiagnosticOwner();

  function detachPending() {
    if (!pending) return;
    const { image, onLoad, onError, completeDecode } = pending;
    if (image.onload === onLoad) image.onload = null;
    if (image.onerror === onError) image.onerror = null;
    pending = null;
    completeDecode?.();
  }

  function cancelPending() {
    generation++;
    detachPending();
  }

  function releaseCanvas() {
    if (!canvas) return;
    if (canvasRef.current === canvas) canvasRef.current = null;
    canvas.width = 0;
    canvas.height = 0;
    canvas = null;
  }

  function discardFailedCanvas() {
    releaseCanvas();
    diagnosticOwner?.dispose();
    diagnosticOwner = createDiagnosticOwner();
  }

  function drawFrame(image: HTMLImageElement) {
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (width <= 0 || height <= 0) {
      throw new Error('Map overlay image has no drawable dimensions.');
    }
    const newlyAllocated = canvas === null;
    canvas ??= document.createElement('canvas');
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    // Count canvas objects once. The byte counter estimates their initial
    // backing stores; the owned gauge below reflects subsequent resizes.
    if (newlyAllocated) memoryDiagnostics.trackCanvas(width, height);
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context)
      throw new Error('Unable to create map overlay hit-test context.');
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    canvasRef.current = canvas;
    diagnosticOwner?.drawn(width, height);
  }

  const load: LoadFunction = (imageWrapper) => {
    if (disposed) return;
    const image = imageWrapper.getImage();
    if (!(image instanceof HTMLImageElement)) {
      throw new Error('Map overlay source did not provide an image element.');
    }
    if (!overlay) {
      // Nothing to draw, but OL still needs this image to resolve.
      image.src = TRANSPARENT_PIXEL_SRC;
      return;
    }

    // A PNG is tied to the viewport that requested it. OL also invokes this
    // loader after panning/zooming without a fresh backend overlay. Those
    // redundant requests must still resolve (assign a src) so OL doesn't
    // wait on this image forever; they just never draw at the wrong extent.
    const resolution = imageWrapper.getResolution();
    const geometry = [
      ...imageWrapper.getExtent(),
      ...(Array.isArray(resolution) ? resolution : [resolution]),
      imageWrapper.getPixelRatio(),
    ];
    const isAuthoritative =
      !consumed &&
      (!requestGeometry || geometryEquals(requestGeometry, geometry));
    if (!isAuthoritative) {
      image.src = UNRESOLVABLE_IMAGE_SRC;
      return;
    }
    cancelPending();
    const requestGeneration = generation;
    // Failed decodes may retry, but never at a different geographic mapping.
    requestGeometry ??= geometry;
    const onLoad = () => {
      if (
        disposed ||
        requestGeneration !== generation ||
        pending?.image !== image
      )
        return;
      try {
        drawFrame(image);
        consumed = true;
      } catch (error) {
        discardFailedCanvas();
        throw error;
      } finally {
        detachPending();
      }
    };
    const onError = () => {
      if (disposed || requestGeneration !== generation) return;
      detachPending();
      console.warn('Unable to decode map overlay image.');
    };
    pending = {
      image,
      onLoad,
      onError,
      completeDecode: diagnosticOwner?.decode(),
    };
    // OL uses addEventListener/decode on this image. Only manage our own
    // property handlers, and install them before a cached/synchronous load.
    image.onload = onLoad;
    image.onerror = onError;
    try {
      refreshSelectedTargets();
      image.src = `data:image/png;base64,${overlay}`;
    } catch (error) {
      detachPending();
      throw error;
    }
  };

  return {
    load,
    /** @returns whether the overlay value actually changed. */
    setOverlay(nextOverlay: string | null) {
      if (disposed || nextOverlay === overlay) return false;
      cancelPending();
      overlay = nextOverlay;
      consumed = false;
      requestGeometry = null;
      if (overlay) {
        diagnosticOwner?.receive();
      } else {
        discardFailedCanvas();
      }
      return true;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      cancelPending();
      overlay = null;
      requestGeometry = null;
      releaseCanvas();
      diagnosticOwner?.dispose();
    },
  };
}
